/**
 * Period Comparison Component
 *
 * Compare two periods (months) for transactions and revenue.
 * Shows side-by-side values with percentage change.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  DollarSign,
  Activity,
  Loader,
  AlertCircle
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { appConfig } from '../config/AppConfig';

const Container = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const PeriodSelectors = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PeriodInput = styled.input`
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  width: 150px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const VsLabel = styled.span`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const CompareButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const MetricCard = styled.div`
  background: ${props => props.bg || '#f8fafc'};
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${props => props.borderColor || '#e2e8f0'};
`;

const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const MetricIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.bg || '#eff6ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || '#3b82f6'};
`;

const MetricTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ValuesRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;
`;

const ValueBox = styled.div`
  text-align: ${props => props.align || 'left'};
`;

const ValueLabel = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const ValueNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
`;

const ChangeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  background: ${props =>
    props.trend === 'up' ? '#f0fdf4' :
    props.trend === 'down' ? '#fef2f2' : '#f8fafc'};
  color: ${props =>
    props.trend === 'up' ? '#15803d' :
    props.trend === 'down' ? '#dc2626' : '#64748b'};
`;

const ChangePercent = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const ChangeAbsolute = styled.span`
  font-size: 13px;
  opacity: 0.8;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #64748b;
  gap: 12px;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  background: #fef2f2;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
`;

const Spinner = styled(Loader)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #64748b;
  font-size: 14px;
`;

function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
}

function formatCurrency(num) {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

function PeriodComparison() {
  const { t, language } = useTranslation();

  // Default to comparing November 2024 vs December 2024 (based on available data)
  // The database contains data from 2024-01-01 to 2024-12-31
  const [periodA, setPeriodA] = useState('2024-11');
  const [periodB, setPeriodB] = useState('2024-12');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);

  const fetchComparison = useCallback(async () => {
    if (!periodA || !periodB) return;

    setLoading(true);
    setError(null);

    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();

      const params = new URLSearchParams({
        db_id: 'kfc_postgres',
        period_a: periodA,
        period_b: periodB,
        metric: 'both'
      });

      const response = await fetch(`${apiUrl}/dashboard/period-comparison?${params}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Request failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get comparison data');
      }

      setComparisonData(data);

    } catch (err) {
      console.error('Comparison fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [periodA, periodB]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchComparison();
  }, []);

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp size={18} />;
    if (trend === 'down') return <TrendingDown size={18} />;
    return <Minus size={18} />;
  };

  return (
    <Container>
      <Header>
        <Title>
          <Calendar size={20} color="#3b82f6" />
          {t('comparison.title')}
        </Title>
        <PeriodSelectors>
          <PeriodInput
            type="month"
            value={periodA}
            onChange={(e) => setPeriodA(e.target.value)}
          />
          <VsLabel>vs</VsLabel>
          <PeriodInput
            type="month"
            value={periodB}
            onChange={(e) => setPeriodB(e.target.value)}
          />
          <CompareButton onClick={fetchComparison} disabled={loading || !periodA || !periodB}>
            {loading ? <Spinner size={16} /> : <Activity size={16} />}
            {language === 'ar' ? 'مقارنة' : 'Compare'}
          </CompareButton>
        </PeriodSelectors>
      </Header>

      {error && (
        <ErrorState>
          <AlertCircle size={18} />
          {error}
        </ErrorState>
      )}

      {loading && (
        <LoadingState>
          <Spinner size={24} />
          {t('status.loading')}
        </LoadingState>
      )}

      {!loading && !error && !comparisonData?.comparison && (
        <NoDataMessage>
          {language === 'ar'
            ? 'اختر فترتين للمقارنة'
            : 'Select two periods to compare'}
        </NoDataMessage>
      )}

      {!loading && !error && comparisonData?.comparison && (
        <ComparisonGrid>
          {/* Transactions Card */}
          {comparisonData.comparison.transactions && (
            <MetricCard bg="#f0f9ff" borderColor="#bae6fd">
              <MetricHeader>
                <MetricIcon bg="#dbeafe" color="#3b82f6">
                  <Activity size={20} />
                </MetricIcon>
                <MetricTitle>{t('comparison.transactions')}</MetricTitle>
              </MetricHeader>

              <ValuesRow>
                <ValueBox>
                  <ValueLabel>{periodA}</ValueLabel>
                  <ValueNumber>
                    {formatNumber(comparisonData.comparison.transactions.period_a_value)}
                  </ValueNumber>
                </ValueBox>
                <ValueBox align="right">
                  <ValueLabel>{periodB}</ValueLabel>
                  <ValueNumber>
                    {formatNumber(comparisonData.comparison.transactions.period_b_value)}
                  </ValueNumber>
                </ValueBox>
              </ValuesRow>

              <ChangeIndicator trend={comparisonData.comparison.transactions.trend}>
                {getTrendIcon(comparisonData.comparison.transactions.trend)}
                <ChangePercent>
                  {comparisonData.comparison.transactions.change_percent > 0 ? '+' : ''}
                  {comparisonData.comparison.transactions.change_percent}%
                </ChangePercent>
                <ChangeAbsolute>
                  ({formatNumber(comparisonData.comparison.transactions.change_absolute)} {t('comparison.change')})
                </ChangeAbsolute>
              </ChangeIndicator>
            </MetricCard>
          )}

          {/* Revenue Card */}
          {comparisonData.comparison.revenue && (
            <MetricCard bg="#fefce8" borderColor="#fde047">
              <MetricHeader>
                <MetricIcon bg="#fef9c3" color="#ca8a04">
                  <DollarSign size={20} />
                </MetricIcon>
                <MetricTitle>{t('comparison.revenue')}</MetricTitle>
              </MetricHeader>

              <ValuesRow>
                <ValueBox>
                  <ValueLabel>{periodA}</ValueLabel>
                  <ValueNumber>
                    {formatCurrency(comparisonData.comparison.revenue.period_a_value)} SAR
                  </ValueNumber>
                </ValueBox>
                <ValueBox align="right">
                  <ValueLabel>{periodB}</ValueLabel>
                  <ValueNumber>
                    {formatCurrency(comparisonData.comparison.revenue.period_b_value)} SAR
                  </ValueNumber>
                </ValueBox>
              </ValuesRow>

              <ChangeIndicator trend={comparisonData.comparison.revenue.trend}>
                {getTrendIcon(comparisonData.comparison.revenue.trend)}
                <ChangePercent>
                  {comparisonData.comparison.revenue.change_percent > 0 ? '+' : ''}
                  {comparisonData.comparison.revenue.change_percent}%
                </ChangePercent>
                <ChangeAbsolute>
                  ({formatCurrency(Math.abs(comparisonData.comparison.revenue.change_absolute))} SAR {t('comparison.change')})
                </ChangeAbsolute>
              </ChangeIndicator>
            </MetricCard>
          )}
        </ComparisonGrid>
      )}
    </Container>
  );
}

export default PeriodComparison;
